<template>
    <div>
        <div class="mb-3">
            <div class="font-weight-bold mb-2">Presets</div>
            <div v-if="presets_loading" class="text-muted">Loading presets...</div>
            <div v-else>
                <div v-if="presets.length" class="mb-2">
                    <b-form-select
                        v-model="activePresetId"
                        :options="preset_options"
                        @change="applyPreset"
                        size="sm"
                    />
                </div>
                <div class="d-flex align-items-center flex-wrap">
                    <b-btn
                        v-if="!creating_preset"
                        size="sm"
                        variant="outline-primary"
                        class="mr-2"
                        @click="startCreatePreset"
                    >
                        Create Preset
                    </b-btn>
                    <div v-else class="d-flex align-items-center mr-2">
                        <b-form-input
                            v-model="new_preset_name"
                            size="sm"
                            placeholder="New preset name"
                            class="mr-2"
                        />
                        <b-btn
                            size="sm"
                            variant="primary"
                            class="mr-2"
                            :disabled="!new_preset_name"
                            @click="createPreset"
                        >
                            Save
                        </b-btn>
                        <b-btn
                            size="sm"
                            variant="outline-secondary"
                            @click="cancelCreatePreset"
                        >
                            Cancel
                        </b-btn>
                    </div>
                    <b-btn
                        size="sm"
                        variant="outline-secondary"
                        class="mr-2"
                        :disabled="!selectedPreset"
                        @click="startRenamePreset"
                    >
                        Rename
                    </b-btn>
                    <b-btn
                        size="sm"
                        variant="outline-danger"
                        :disabled="!selectedPreset"
                        @click="deletePreset"
                    >
                        Delete
                    </b-btn>
                </div>
                <b-form-input
                    v-if="renaming_preset"
                    v-model="rename_preset_name"
                    size="sm"
                    placeholder="Rename preset"
                    class="mt-2"
                />
                <div v-if="renaming_preset" class="mt-2 d-flex align-items-center">
                    <b-btn
                        size="sm"
                        variant="primary"
                        class="mr-2"
                        :disabled="!rename_preset_name"
                        @click="renamePreset"
                    >
                        Save
                    </b-btn>
                    <b-btn
                        size="sm"
                        variant="outline-secondary"
                        @click="cancelRenamePreset"
                    >
                        Cancel
                    </b-btn>
                </div>
                <div v-if="show_apply_preset_changes" class="mt-2">
                    <b-alert show variant="warning" class="mb-2">
                        Filters no longer match the active preset.
                    </b-alert>
                    <b-btn
                        size="sm"
                        variant="warning"
                        @click="applyChangesToPreset"
                    >
                        Update Active Preset
                    </b-btn>
                </div>
            </div>
        </div>
        <div class="font-weight-bold mb-2">Filters</div>
        <div v-for="group in element_visibility_groups" :key="group.group" class="mb-3">
            <div class="font-weight-bold mb-2">{{ group.group }}</div>
            <b-form-checkbox
                v-for="item in group.items"
                :key="item.key"
                :checked="isElementVisible(element_visibility, item.key)"
                @change="toggleElementVisibility(item.key, $event)"
                switch
            >
                {{ item.label }}
            </b-form-checkbox>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { ELEMENT_VISIBILITY_GROUPS, getElementVisibilityDefaults, isElementVisible } from '@/common/elementVisibility'

export default {
    data: () => ({
        new_preset_name: '',
        rename_preset_name: '',
        creating_preset: false,
        renaming_preset: false,
    }),
    computed: {
        ...mapState({
            element_visibility: state => state.settings.element_visibility,
            presets: state => state.settings.presets,
            presets_loading: state => state.settings.presets_loading,
            active_preset_id: state => state.settings.active_preset_id,
        }),
        element_visibility_groups () {
            return ELEMENT_VISIBILITY_GROUPS
        },
        preset_options () {
            const options = this.presets.map(preset => ({
                value: preset.id,
                text: preset.name,
            }))
            return [
                { value: 'all', text: 'No Preset (Show All)' },
                { value: '__divider__', text: '──────────', disabled: true },
                { value: null, text: 'Select a preset' },
                ...options
            ]
        },
        activePresetId: {
            get () {
                return this.active_preset_id
            },
            set (value) {
                this.$store.commit('setActivePresetId', value)
            }
        },
        selectedPreset () {
            return this.presets.find(preset => preset.id === this.active_preset_id) || null
        },
        show_apply_preset_changes () {
            if (!this.selectedPreset || this.active_preset_id === 'all' || this.creating_preset || this.renaming_preset) {
                return false
            }
            const defaults = getElementVisibilityDefaults()
            const normalize = (map) => {
                const normalized = {}
                const source = map && typeof map === 'object' ? map : {}
                for (const key of Object.keys(defaults)) {
                    normalized[key] = Object.prototype.hasOwnProperty.call(source, key) ? source[key] : defaults[key]
                }
                return normalized
            }
            const current = normalize(this.element_visibility)
            const selected = normalize(this.selectedPreset.preset)
            return JSON.stringify(current) !== JSON.stringify(selected)
        },
    },
    methods: {
        isElementVisible,
        toggleElementVisibility (key, value) {
            this.$store.dispatch('setElementVisibility', { key, value })
        },
        startCreatePreset () {
            this.creating_preset = true
            this.new_preset_name = ''
        },
        cancelCreatePreset () {
            this.creating_preset = false
            this.new_preset_name = ''
        },
        async fetchPresets () {
            await this.$store.dispatch('fetchPresets')
            this.rename_preset_name = this.selectedPreset ? this.selectedPreset.name : ''
        },
        async createPreset () {
            const name = this.new_preset_name && this.new_preset_name.trim()
            if (!name) {
                return
            }
            const payload = {
                name,
                filter_type: 'publications',
                preset: this.element_visibility || {},
            }
            const created = await this.$store.dispatch('createPreset', payload)
            if (!created) {
                return
            }
            this.rename_preset_name = created.name
            this.new_preset_name = ''
            this.creating_preset = false
        },
        async applyPreset () {
            if (this.active_preset_id === 'all') {
                await this.$store.dispatch('clearActivePreset', 'publications')
                this.$store.dispatch('setElementVisibilityMap', getElementVisibilityDefaults())
                this.renaming_preset = false
                this.creating_preset = false
                return
            }
            const preset = this.selectedPreset
            if (!preset) {
                return
            }
            await this.$store.dispatch('applyPreset', preset.id)
            this.rename_preset_name = preset.name
            this.renaming_preset = false
            this.creating_preset = false
        },
        startRenamePreset () {
            if (!this.selectedPreset) {
                return
            }
            this.renaming_preset = true
            this.rename_preset_name = this.selectedPreset.name
        },
        cancelRenamePreset () {
            this.renaming_preset = false
            this.rename_preset_name = this.selectedPreset ? this.selectedPreset.name : ''
        },
        async renamePreset () {
            const preset = this.selectedPreset
            const name = this.rename_preset_name && this.rename_preset_name.trim()
            if (!preset || !name) {
                return
            }
            const confirmed = await this.$swal({
                title: 'Rename preset?',
                text: `Rename "${preset.name}" to "${name}"?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Rename',
                cancelButtonText: 'Cancel',
            })
            if (!confirmed || !confirmed.isConfirmed) {
                return
            }
            await this.$store.dispatch('renamePreset', { id: preset.id, name })
            this.renaming_preset = false
        },
        async deletePreset () {
            const preset = this.selectedPreset
            if (!preset) {
                return
            }
            const confirmed = await this.$swal({
                title: 'Delete preset?',
                text: `Delete "${preset.name}"?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Delete',
                cancelButtonText: 'Cancel',
            })
            if (!confirmed || !confirmed.isConfirmed) {
                return
            }
            const active = await this.$store.dispatch('deletePreset', preset.id)
            this.rename_preset_name = active ? active.name : ''
            this.renaming_preset = false
            this.creating_preset = false
        },
        async applyChangesToPreset () {
            if (!this.selectedPreset) {
                return
            }
            const presetPayload = this.element_visibility || {}
            const response = await this.axios.patch(`/api/user_view_filter_presets/${this.selectedPreset.id}`, {
                preset: presetPayload,
            })
            const updatedPreset = response.data && response.data.preset ? response.data.preset : null
            if (!updatedPreset) {
                return
            }
            const updated = this.presets.map(preset => (
                preset.id === updatedPreset.id ? updatedPreset : preset
            ))
            this.$store.commit('setPresets', updated)
        }
    },
    watch: {
        active_preset_id () {
            this.rename_preset_name = this.selectedPreset ? this.selectedPreset.name : ''
            this.renaming_preset = false
            this.creating_preset = false
        },
        presets () {
            this.rename_preset_name = this.selectedPreset ? this.selectedPreset.name : ''
            this.renaming_preset = false
            this.creating_preset = false
        }
    },
    mounted () {
        this.$store.dispatch('initElementVisibility')
        this.fetchPresets()
    }
}
</script>
